/**
 * Broker Activity Monitor — Goodwin charter-marketplace demo.
 *
 * Raw shapes mirror `backend/services/broker_monitor/service.py` responses
 * exactly. Adapter functions below map them onto the generic chain-monitor
 * component types (`components/chain-monitor/types.ts`) so this page can
 * reuse those components without them knowing anything Goodwin-specific.
 */
import type {
  ChainEntitySummary,
  ChainMetric,
  ChainPivotPanel,
  ChainPivotRow,
  ChainRootCausePanel,
  ChainSnapshot,
  ChainStage,
} from '../components/chain-monitor/types';

export interface BMPeriod {
  start?: string | null;
  end?: string | null;
}

export interface BMStage {
  id: string;
  name: string;
  order: number;
  status: 'green' | 'yellow' | 'red';
  metric: ChainMetric;
}

export interface BMChainSnapshot {
  data_available: boolean;
  comparison_label: string;
  current_period: BMPeriod;
  prior_period: BMPeriod;
  as_of_date?: string | null;
  overall_status: 'green' | 'yellow' | 'red';
  stages: BMStage[];
}

export interface BMBrokerSummary {
  broker_id: string;
  broker_name: string;
  market?: string | null;
  broker_status?: string | null;
  status: 'green' | 'yellow' | 'red';
  label: string;
  sufficient_data: boolean;
  request_metric: ChainMetric;
  booking_metric: ChainMetric;
}

export interface BMBrokerList {
  current_period: BMPeriod;
  prior_period: BMPeriod;
  comparison_label: string;
  brokers: BMBrokerSummary[];
}

export interface BMTopOperator {
  operator_id: string;
  operator_name: string;
  request_count: number;
  share_pct: number;
  is_dominant: boolean;
}

export interface BMRootCause {
  broker_id: string;
  broker_name: string;
  cause: 'operator' | 'price' | 'unknown';
  what_happened: string;
  why: string;
  suggestion: string;
  top_operator: BMTopOperator | null;
  method_note?: string | null;
}

export interface BMBrokerDetail {
  broker_id: string;
  broker_name: string;
  market?: string | null;
  join_date?: string | null;
  broker_status?: string | null;
  current_period: BMPeriod;
  prior_period: BMPeriod;
  comparison_label: string;
  overall_status: 'green' | 'yellow' | 'red';
  overall_label: string;
  sufficient_data: boolean;
  stages: BMStage[];
  top_operator: BMTopOperator | null;
  operator_metric: ChainMetric | null;
  root_cause: BMRootCause;
}

export interface BMPerBrokerRow extends ChainMetric {
  broker_id: string;
  broker_name: string;
}

export interface BMCrossBrokerPanel {
  operator_id: string;
  operator_name: string;
  marketplace_wide: boolean;
  affected_broker_count: number;
  total_broker_count: number;
  headline: string;
  aggregate_metric: ChainMetric;
  per_broker: BMPerBrokerRow[];
  method_note?: string | null;
}

export interface BMOperatorCrossBroker {
  operator_id: string;
  operator_name: string;
  api_enabled: boolean;
  region?: string | null;
  current_period: BMPeriod;
  prior_period: BMPeriod;
  comparison_label: string;
  panel: BMCrossBrokerPanel;
}

// ── Adapters onto the generic chain-monitor component types ────────────────

export function toChainSnapshot(snapshot: BMChainSnapshot): ChainSnapshot {
  return {
    data_available: snapshot.data_available,
    comparison_label: snapshot.comparison_label,
    current_period: snapshot.current_period,
    prior_period: snapshot.prior_period,
    as_of_date: snapshot.as_of_date,
    overall_status: snapshot.overall_status,
    stages: snapshot.stages as ChainStage[],
  };
}

export function brokersToEntities(brokers: BMBrokerSummary[]): ChainEntitySummary[] {
  return brokers.map((b) => ({
    entity_id: b.broker_id,
    entity_name: b.broker_name,
    status: b.status,
    label: b.label,
    sufficient_data: b.sufficient_data,
    subtitle: b.market ?? undefined,
    metrics: [b.booking_metric, b.request_metric],
  }));
}

export function toRootCausePanel(rc: BMRootCause): ChainRootCausePanel {
  return {
    entity_id: rc.broker_id,
    entity_name: rc.broker_name,
    cause: rc.cause,
    what_happened: rc.what_happened,
    why: rc.why,
    suggestion: rc.suggestion,
    method_note: rc.method_note,
  };
}

export function toCrossPivotPanel(panel: BMCrossBrokerPanel): ChainPivotPanel {
  const rows: ChainPivotRow[] = panel.per_broker.map((row) => ({
    key: row.broker_id,
    label: row.broker_name,
    status: row.status,
    pct_change: row.pct_change,
    value: row.value,
    prior_value: row.prior_value,
    format: row.format,
  }));
  return {
    pivot_id: panel.operator_id,
    pivot_name: `${panel.operator_name} (${panel.operator_id}) — cross-broker response time`,
    headline: panel.headline,
    is_widespread: panel.marketplace_wide,
    affected_count: panel.affected_broker_count,
    total_count: panel.total_broker_count,
    aggregate_metric: panel.aggregate_metric,
    rows,
    method_note: panel.method_note,
  };
}
